import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import AlertDialog from "../../../../components/AlertDialog";
import { logoutUser } from "../../../../slices/auth/authenticationSlice";
import { getLmsRole, isParentRole } from "../../utils/lmsRole";
import {
    createContent,
    deleteContent,
    getContentById,
    getContents,
    updateContent,
} from "../controllers/contentController";
import ContentCard from "../components/ContentCard";
import ContentEmptyState from "../components/ContentEmptyState";
import ContentFormModal from "../components/ContentFormModal";
import ContentPreviewModal from "../components/ContentPreviewModal";
import { ContentLibraryWrapper } from "../styles/ContentLibraryStyle";
import {
    buildCreateContentPayload,
    buildUpdateContentPayload,
    contentToDraft,
    emptyContentDraft,
    getContentErrorMessage,
    normalizeContentListResponse,
    normalizeSingleContentResponse,
    validateContentDraft,
} from "../utils/contentUtils";

const ContentLibraryPage = () => {
    const dispatch = useDispatch();
    const { credObj } = useSelector((store) => store.auth);
    const { currentUser } = useSelector((store) => store.users);
    const role = getLmsRole({ currentUser, credObj });
    const isParent = isParentRole(role);
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState("");
    const [editingItem, setEditingItem] = useState(null);

    const [previewItem, setPreviewItem] = useState(null);

    const [deletingItem, setDeletingItem] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleApiFailure = useCallback(
        (apiError, setLocalError) => {
            if (apiError?.status === 401) {
                dispatch(logoutUser());
                window.location.assign("/lms/login");
                return;
            }

            const message = getContentErrorMessage(apiError);
            if (setLocalError) {
                setLocalError(message);
            } else {
                setError(message);
            }
        },
        [dispatch]
    );

    const loadContents = useCallback(async () => {
        setIsLoading(true);
        setError("");
        try {
            const response = await getContents();
            setItems(normalizeContentListResponse(response));
        } catch (apiError) {
            handleApiFailure(apiError);
        } finally {
            setIsLoading(false);
        }
    }, [handleApiFailure]);

    useEffect(() => {
        loadContents();
    }, [loadContents]);

    const openCreate = () => {
        setEditingItem(null);
        setFormError("");
        setIsFormOpen(true);
    };

    const openEdit = async (item) => {
        setFormError("");
        setIsSubmitting(true);
        try {
            const response = await getContentById(item.id);
            setEditingItem(normalizeSingleContentResponse(response));
            setIsFormOpen(true);
        } catch (apiError) {
            handleApiFailure(apiError, setFormError);
            toast.error(getContentErrorMessage(apiError));
        } finally {
            setIsSubmitting(false);
        }
    };

    const openPreview = async (item) => {
        try {
            const response = await getContentById(item.id);
            setPreviewItem(normalizeSingleContentResponse(response));
        } catch (apiError) {
            handleApiFailure(apiError);
            toast.error(getContentErrorMessage(apiError));
        }
    };

    const handleSubmitForm = async (draft) => {
        const validation = validateContentDraft(draft);
        if (!validation.isValid) {
            return;
        }

        setFormError("");
        setIsSubmitting(true);

        try {
            if (editingItem) {
                const payload = buildUpdateContentPayload(editingItem, draft);

                if (!Object.keys(payload).length) {
                    toast.info("No content changes detected.");
                    setIsFormOpen(false);
                    setEditingItem(null);
                    return;
                }

                await updateContent(editingItem.id, payload);
                toast.success("Reading content updated");
            } else {
                await createContent(buildCreateContentPayload(draft));
                toast.success("Reading content created");
            }

            setIsFormOpen(false);
            setEditingItem(null);
            await loadContents();
        } catch (apiError) {
            handleApiFailure(apiError, setFormError);
        } finally {
            setIsSubmitting(false);
        }
    };

    const closeForm = () => {
        if (isSubmitting) return;
        setIsFormOpen(false);
        setEditingItem(null);
        setFormError("");
    };

    const askDelete = (item) => {
        setDeletingItem(item);
    };

    const confirmDelete = async () => {
        if (!deletingItem) return;

        setIsDeleting(true);
        try {
            await deleteContent(deletingItem.id);
            toast.success("Reading content deleted");
            setDeletingItem(null);
            await loadContents();
        } catch (apiError) {
            handleApiFailure(apiError);
            toast.error(getContentErrorMessage(apiError));
        } finally {
            setIsDeleting(false);
        }
    };

    const formDraft = useMemo(
        () => (editingItem ? contentToDraft(editingItem) : emptyContentDraft),
        [editingItem]
    );

    return (
        <ContentLibraryWrapper>
            <section className="content-header">
                <div>
                    <p className="eyebrow">Reading Library</p>
                    <h1>Build content your learners will remember</h1>
                    <p>Create, preview, and refine reading content before assigning it.</p>
                </div>
                <button type="button" className="lms-primary-action" onClick={openCreate}>
                    Create content
                </button>
            </section>

            {error && (
                <p className="content-error" role="alert">
                    {error}
                </p>
            )}

            {isLoading && <p className="content-loading">Loading reading content...</p>}

            {!isLoading && !error && items.length === 0 && (
                <ContentEmptyState onCreate={openCreate} learnerPlural={isParent ? "kids" : "students"} />
            )}

            {!isLoading && !error && items.length > 0 && (
                <section className="content-list" aria-label="Reading content list">
                    {items.map((item) => (
                        <ContentCard
                            key={item.id}
                            item={item}
                            onPreview={openPreview}
                            onEdit={openEdit}
                            onDelete={askDelete}
                        />
                    ))}
                </section>
            )}

            <ContentFormModal
                open={isFormOpen}
                title={editingItem ? "Edit reading content" : "Create reading content"}
                initialDraft={formDraft}
                error={formError}
                isSubmitting={isSubmitting}
                onClose={closeForm}
                onSubmit={handleSubmitForm}
            />

            <ContentPreviewModal content={previewItem} onClose={() => setPreviewItem(null)} />

            <AlertDialog
                open={Boolean(deletingItem)}
                onClose={() => (isDeleting ? null : setDeletingItem(null))}
                title="Delete reading content"
                description={`Delete \"${deletingItem?.title || "this content"}\"? This action can be undone by support only.`}
                agreeText={isDeleting ? "Deleting..." : "Delete"}
                disagreeText="Cancel"
                runTaskAndClose={confirmDelete}
                agreeDisabled={isDeleting}
            />
        </ContentLibraryWrapper>
    );
};

export default ContentLibraryPage;
